import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Draws a Polygon2D as a CanvasItem2D whose body keeps one vertex per `polygon`
 * point, triangulated by index: `uv` and `vertex_colors` pair with points by
 * position, so a renumbering mesh such as `ShapeGeometry` would mismatch them.
 */
import { useMemo } from 'react';
import * as THREE from 'three';
import { CanvasItem2D } from '../../../r3f/components/CanvasItem2D';
import { multiplyModulate } from '../../../r3f/canvasItemModulate';
import { godotColorToLinear } from '../../../r3f/godotColor';
import { useCanvas2DTexture } from '../../../r3f/canvas2DTextureDecode';
import { useNode2DTextureSampler } from '../../../r3f/canvasItemTextureSampler';
import { CANVAS_SRGB_DEFINES } from '../../../r3f/canvasSrgbMultiply';
import { materialProgramInputs } from '../../../r3f/materialProgramInputs';
import { useTexture2D } from '../../../resources/useTexture2D';
import { useSceneResources } from '../../../r3f/SceneResourcesContext';
import { polygonRings } from './polygonShapes';
import { quantizeVertexColor } from './vertexColorQuantize';
import { useDisposeOnRelease } from '../../../r3f/useDisposeOnRelease';
export function Polygon2D({ node, children }) {
    const props = node.properties;
    const { externalResources, internalResources } = useSceneResources();
    // Either an image file or an inline procedural texture; `useTexture2D` hides
    // which, and owns the lifetime of the procedural one it rasterises.
    const { texture: resolvedTexture, size } = useTexture2D(props.texture, externalResources, internalResources);
    const sampler = useNode2DTextureSampler(props);
    const texture = useCanvas2DTexture(resolvedTexture, sampler);
    const rings = useMemo(() => polygonRings(props.polygon, props.polygons, props.internalVertexCount, props.invertEnabled, props.invertBorder), [props.polygon, props.polygons, props.internalVertexCount, props.invertEnabled, props.invertBorder]);
    // Godot divides the transformed UV by the texture's size, so the geometry rebuilds when a late
    // load changes the dimensions.
    const uvSpace = texture ? (size ?? null) : null;
    const geometry = useMemo(() => buildPolygonGeometry(rings, props, uvSpace), [rings, props, uvSpace]);
    // R3F does not dispose a geometry passed through `attach`, so release it on rebuild.
    useDisposeOnRelease(geometry);
    return (_jsx(CanvasItem2D, { node: node, props: props, body: (tint, _material, pixelMaterial) => geometry ? (_jsx(FilledPolygon, { geometry: geometry, tint: tint, color: props.color, texture: texture, vertexColors: geometry.hasAttribute('color'), pixelMaterial: pixelMaterial })) : null, children: children }));
}
function FilledPolygon({ geometry, tint, color, texture, vertexColors, pixelMaterial, }) {
    // Godot multiplies fill × modulate × self_modulate in sRGB, then converts once.
    // `color` is quantized first, as Godot's 8-bit mesh upload stores it
    // (`vertexColorQuantize.ts`). The inherited tint (`tint.own`) is an
    // unquantized per-draw multiply, so it composes after.
    const { fill, tintOnlyFill, opacity, tintOnlyOpacity } = useMemo(() => {
        const composed = multiplyModulate(tint.own, quantizeVertexColor(color));
        return {
            fill: godotColorToLinear(composed),
            // The same tint without the node's own `color`, for the per-vertex path
            // where Godot replaces `color` rather than combining with it.
            tintOnlyFill: godotColorToLinear(tint.own),
            opacity: composed.a,
            // `color` drops whole, alpha included, on the vertex-colour path:
            // `polygon_2d.cpp:310-314` assigns the vertex Color outright, and
            // `canvas_item_add_mesh` gets a bare `Color(1, 1, 1)` (`polygon_2d.cpp:401`).
            tintOnlyOpacity: tint.own.a,
        };
    }, [tint.own, color]);
    // `USE_MAP` is baked into the program source, so without
    // `materialProgramInputs` a texture resolving after the first compile would
    // never reach the shader.
    const program = materialProgramInputs({
        props: {
            // Godot's draw picks one: `vertex_colors[i]` when the sizes match, else
            // `color`. three multiplies this into vColor, so passing the fill too would
            // render `color x vertexColor`. The node tint still applies.
            color: vertexColors ? tintOnlyFill : fill,
            map: texture,
            vertexColors,
            opacity: vertexColors ? tintOnlyOpacity : opacity,
            transparent: true,
            depthWrite: false,
            // The `NoColorSpace` map and the sRGB vertex colours, which Godot multiplies as stored (`canvas.glsl:631`).
            defines: CANVAS_SRGB_DEFINES,
        },
        // The one canvas mesh where a facing split would be visible, since every
        // vertex carries its own colour and alpha. It is safe only because earcut
        // (`THREE.ShapeUtils.triangulateShape`) winds every ring one way, a property
        // `triangulateRing` does not ask for.
        merge: [pixelMaterial],
    });
    return (_jsxs("mesh", { children: [_jsx("primitive", { object: geometry, attach: "geometry" }), _jsx("meshBasicMaterial", { ...program.props }, program.key)] }));
}
/**
 * Builds the mesh from the resolved rings, or null with no fillable ring.
 * `polygons` yields independent rings, and `invert_enabled` the grown bounds with
 * the polygon as a hole. Winding is irrelevant: the material is double-sided.
 */
function buildPolygonGeometry(rings, props, uvSpace) {
    if (rings.outlines.length === 0)
        return null;
    // Godot's `points[i] = polygon[i] + offset`, and the UV fallback reads those
    // offset points, so the offset is folded in first.
    const points = rings.points.map((p) => ({
        x: p.x + props.offset.x,
        y: p.y + props.offset.y,
    }));
    const index = [];
    const holes = rings.hole ? [rings.hole] : [];
    for (const outline of rings.outlines) {
        triangulateRing(points, outline, holes, index);
    }
    if (index.length === 0)
        return null;
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(points.length * 3);
    for (let i = 0; i < points.length; i++) {
        positions[i * 3] = points[i].x;
        positions[i * 3 + 1] = -points[i].y;
        positions[i * 3 + 2] = 0;
    }
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setIndex(index);
    if (uvSpace) {
        geometry.setAttribute('uv', new THREE.BufferAttribute(buildUvs(points, props, uvSpace), 2));
    }
    const colors = buildVertexColors(points.length, props.vertexColors);
    if (colors)
        geometry.setAttribute('color', new THREE.BufferAttribute(colors, 4));
    return geometry;
}
/**
 * Godot's per-vertex UV (`polygon_2d.cpp`): `texmat.xform(uv[i] or points[i]) /
 * tex_size`, with `texmat = Transform2D(tex_rot, tex_ofs).scale(tex_scale)`.
 * `scale` scales the origin too, which the expanded form keeps visible.
 */
function buildUvs(points, props, uvSpace) {
    // Godot divides by a size 0 (`polygon_2d.cpp:224-237`), and its draw samples the top-left texel.
    if (uvSpace.width === 0 || uvSpace.height === 0)
        return topLeftUvs(points.length);
    const cos = Math.cos(props.textureRotation);
    const sin = Math.sin(props.textureRotation);
    const { x: sx, y: sy } = props.textureScale;
    const { x: ox, y: oy } = props.textureOffset;
    // The authored `uv` applies only when there is exactly one per vertex;
    // otherwise Godot texture-maps the point coordinates themselves.
    const authored = props.uv.length / 2 === points.length ? props.uv : null;
    const out = new Float32Array(points.length * 2);
    for (let i = 0; i < points.length; i++) {
        const vx = authored ? authored[i * 2] : points[i].x;
        const vy = authored ? authored[i * 2 + 1] : points[i].y;
        out[i * 2] = (sx * (cos * vx - sin * vy + ox)) / uvSpace.width;
        // Godot's texel space runs +Y down from the top-left, and three samples a
        // `flipY` texture with v = 1 at the top row. The flip comes after the whole
        // Godot-side transform, never inside it, to keep the image upright.
        out[i * 2 + 1] = 1 - (sy * (sin * vx + cos * vy + oy)) / uvSpace.height;
    }
    return out;
}
/**
 * Per-vertex RGBA, only when there is exactly one per vertex: Godot falls back to
 * the flat `color` otherwise. itemSize 4 makes three define `USE_COLOR_ALPHA`, so
 * an authored alpha fade renders, where a three-wide attribute would not.
 */
function buildVertexColors(vertexCount, vertexColors) {
    if (vertexCount === 0 || vertexColors.length / 4 !== vertexCount)
        return null;
    const out = new Float32Array(vertexCount * 4);
    for (let i = 0; i < vertexCount; i++) {
        // Each entry passes the same truncating 8-bit upload as the flat `color`
        // (`polygon_2d.cpp:310-314` fills the same `Vector<Color>`), and stays sRGB.
        const stored = quantizeVertexColor({
            r: vertexColors[i * 4],
            g: vertexColors[i * 4 + 1],
            b: vertexColors[i * 4 + 2],
            a: vertexColors[i * 4 + 3],
        });
        out[i * 4] = stored.r;
        out[i * 4 + 1] = stored.g;
        out[i * 4 + 2] = stored.b;
        out[i * 4 + 3] = stored.a;
    }
    return out;
}
/**
 * Triangulate one ring (with optional holes) and append the triangles to
 * `index`, in the original point numbering. `ShapeUtils.triangulateShape`
 * returns triples indexing the contour arrays it was given, so they are mapped
 * back through the ring's own indices.
 */
function triangulateRing(points, outline, holes, index) {
    const contour = outline.map((i) => new THREE.Vector2(points[i].x, -points[i].y));
    const holeContours = holes.map((hole) => hole.map((i) => new THREE.Vector2(points[i].x, -points[i].y)));
    // The flat local numbering triangulateShape works in: contour first, then
    // each hole, in the order it concatenates them internally.
    const localToGlobal = [...outline, ...holes.flat()];
    for (const tri of THREE.ShapeUtils.triangulateShape(contour, holeContours)) {
        for (const local of tri) {
            const global = localToGlobal[local];
            if (global === undefined)
                return;
            index.push(global);
        }
    }
}
/** The top-left texel's UV, u 0 and v 1, at every vertex. */
function topLeftUvs(vertexCount) {
    const out = new Float32Array(vertexCount * 2);
    for (let i = 0; i < vertexCount; i++)
        out[i * 2 + 1] = 1;
    return out;
}
//# sourceMappingURL=Component.js.map